import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import Heading from "../../../../../../common/element/Heading/index.js";
import BtnLight from "../../../../../../common/element/Buttons/btnLight.js";
import Dropdown from "../../../../../../common/element/Dropdown/Dropdown.js";
import { setBreadcrumbs } from "../../../../../../redux/classes/breadcrumbsSlice.js";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Pagination } from "../../../../../../common/pagination/index.js";
import useUnauthorizedHandler from "../../../../../../common/snippet/useUnauthorizedHandler.js";
import ThreeDotBouncing from "../../../../../../common/element/PageLoader/index.js";
import ThreeDotLoader from "../../../../../../common/element/ButtonLoader/ThreeDotLoader";
import { notify } from "react-notify-toast";
import ReactPaginate from "react-paginate";
import AllProviderMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/AllProviderMultiSelectDropdown.js";
import ClientMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/ClientMultiSelectDropdown.js";
import MainOfficeStateMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/MainOfficeStateMultiSelectDropdown.js";
import StatusMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/StatusMultiSelectDropdown.js";
import DateMultiSelectDropdown from "../../../../../../common/MultiSelectFilter/DateMultiSelectDropdown.js";
import moment from "moment";
import { CSVLink } from "react-csv";
import logoPDF from "../ProvidersComplianceChecklist/LogoPDF.png";
import Input from "../../../../../../common/element/Input/Input.js";
import Table from "../../../../../../common/element/Table";
import { GenerateProviderExpiryReport } from "../../../../../../actions/admin-provider/reports/GenerateProviderExpiryReport.js";

const { Tr, Th, Td, THead, TBody } = Table;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatAmount = (raw) => {
  if (raw == null || raw === "") return "-";
  const n = Number(String(raw).replace(/[$,\s]/g, ""));
  if (!Number.isFinite(n)) return String(raw);
  return `$${n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const parseFields = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object") return raw;
  try { return JSON.parse(raw); } catch { return {}; }
};

const getIssueDate = (row) => {
  const f = parseFields(row?.doctype_fields_data);
  return f?.issuedate || f?.IssueDate || f?.issueDate || null;
};

const getExpiry = (row) => row?.approval_expiry_date || row?.doc_expiry || null;

const isExpiredRow = (row) => {
  const exp = getExpiry(row);
  return exp && moment(exp).isValid() && moment(exp).isBefore(moment(), "day");
};

const statusLabel = (s, row) => {
  if (row && isExpiredRow(row)) return "Action Required";
  if (!s) return "-";
  const map = {
    approved: "Approved",
    pending: "Pending",
    client_approval_req: "Client Approval Req.",
    client_approved_action: "Client Approved Action",
    admin_reject: "Admin Rejected",
    client_reject: "Client Rejected",
  };
  return map[s] || s;
};

const statusBadgeClass = (s, row) => {
  if (row && isExpiredRow(row)) return "badge bg-error-100 text-error-700 py-0.5 px-2 rounded-lg font-medium";
  if (s === "approved") return "badge bg-success-50 text-success-700 py-0.5 px-2 rounded-lg font-medium";
  if (s === "admin_reject" || s === "client_reject") return "badge bg-error-100 text-error-700 py-0.5 px-2 rounded-lg font-medium";
  return "badge bg-warning-50 text-warning-700 py-0.5 px-2 rounded-lg font-normal";
};

function getBase64ImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.setAttribute("crossOrigin", "anonymous");
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Component ────────────────────────────────────────────────────────────────
const ProvidersExpiry = ({ portalType = "admin" }) => {
  const dispatch = useDispatch();
  const handleUnauthorizedAccess = useUnauthorizedHandler();
  const csvLinkRef = useRef(null);
  const actualPortalType = portalType === "clientPortal" ? "client" : portalType;

  const [reportData, setReportData] = useState(null);
  const [loadData, setLoadData] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [processedCsvData, setProcessedCsvData] = useState([]);
  const [pdfDownloadSuccessDialog, setPdfDownloadSuccessDialog] = useState(false);
  const [checkPdfLoad, setCheckPdfLoad] = useState(true);
  const [successText, setSuccessText] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [dateMinMax] = useState({ lowestDate: null, greatestDate: null });
  const [key, setKey] = useState("custom");
  const [expandedKeys, setExpandedKeys] = useState(new Set());

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    setExpandedKeys(new Set());
  }, [reportData]);

  const [getAllinvitedData, SetgetAllinvitedData] = useState(() => {
    const storedData = JSON.parse(localStorage.getItem("LoginUserData"));
    const orgId = storedData?.organisation_id;
    return {
      search: "",
      approval_status: "",
      provider_org_ids: [],
      client_org_ids: (portalType === "clientPortal" && orgId) ? [orgId] : [],
      state_ids: [],
      client_contact_name: "",
      doc_name: "",
      expiry_date_range: "",
      page: 1,
      limit: 10,
      sort: "provider_name",
      order: "asc",
    };
  });

  const clientOrgIdsForProvider = useMemo(() => {
    const storedData = JSON.parse(localStorage.getItem("LoginUserData"));
    const orgId = storedData?.organisation_id;
    if (portalType === "clientPortal" && orgId) {
      return [orgId];
    }
    return (getAllinvitedData?.client_org_ids || []).map(
      (item) => item?.organisation_id ?? item
    );
  }, [portalType, getAllinvitedData?.client_org_ids]);

  const debouncedContactNameSearch = useCallback(
    debounce((val) => {
      updateFilter("client_contact_name", val);
    }, 500),
    []
  );

  const debouncedDocNameSearch = useCallback(
    debounce((val) => {
      updateFilter("doc_name", val);
    }, 500),
    []
  );

  // ─── Breadcrumbs ───────────────────────────────────────────────────────────
  useEffect(() => {
    let reportsUrl = "/admin/provider/reports";
    let homeUrl = "/admin/provider/dashboard";
    if (portalType === "client") { reportsUrl = "/admin/client/reports"; homeUrl = "/admin/client/dashboard"; }
    else if (portalType === "clientPortal") { reportsUrl = "/client/reports"; homeUrl = "/client/dashboard"; }
    dispatch(setBreadcrumbs([
      { label: "Reports", url: reportsUrl, home: homeUrl },
      { label: "Provider Expiry Report", url: "" },
    ]));
  }, [dispatch, portalType]);

  // ─── Fetch ─────────────────────────────────────────────────────────────────
  const fetchReport = useCallback(async (filters) => {
    if (!filters) return null;
    setLoadData(false);
    try {
      const payload = {
        ...filters,
        group_by_provider: true,
        provider_org_ids: filters.provider_org_ids?.length
          ? JSON.stringify(filters.provider_org_ids.map((i) => i?.provider_org_id ?? i))
          : undefined,
        client_org_ids: filters.client_org_ids?.length
          ? JSON.stringify(filters.client_org_ids.map((i) => i?.organisation_id ?? i))
          : undefined,
        state_ids: filters.state_ids?.length ? JSON.stringify(filters.state_ids) : undefined,
      };
      const res = await dispatch(GenerateProviderExpiryReport(payload, actualPortalType));
      if (res?.success) {
        setReportData(res.data);
        const limit = Number(filters.limit) || 10;
        setTotalPages(Math.ceil((res.data?.total || 0) / limit));
      } else if (res?.status === 401) {
        handleUnauthorizedAccess();
      }
      return res;
    } catch (e) {
      console.error(e);
      return null;
    } finally {
      setLoadData(true);
    }
  }, [dispatch, actualPortalType, handleUnauthorizedAccess]);

  const latestFiltersRef = useRef(null);
  const loadingTimeoutRef = useRef();
  const isInitialRef = useRef(true);

  useEffect(() => {
    const stringified = JSON.stringify({ ...getAllinvitedData, _rk: refreshKey });
    if (isInitialRef.current) {
      isInitialRef.current = false;
      latestFiltersRef.current = stringified;
      fetchReport(getAllinvitedData);
      return;
    }
    if (latestFiltersRef.current === stringified) return;
    latestFiltersRef.current = stringified;
    clearTimeout(loadingTimeoutRef.current);
    const snapshot = JSON.parse(stringified);
    loadingTimeoutRef.current = setTimeout(() => fetchReport(snapshot), 150);
    return () => clearTimeout(loadingTimeoutRef.current);
  }, [getAllinvitedData, fetchReport, refreshKey]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const updateFilter = (fieldName, value) => {
    SetgetAllinvitedData((prev) => {
      const normalize = (input) => {
        if (!Array.isArray(input)) return input;
        return input
          .map((item) => {
            if (item && typeof item === "object") {
              if (fieldName === "provider_org_ids") return item?.provider_org_id ?? item;
              if (fieldName === "client_org_ids") return item?.organisation_id ?? item;
              if (fieldName === "state_ids") return item?.state_id ?? item;
            }
            return item;
          })
          .filter((e) => e !== undefined && e !== null);
      };
      const prevNorm = normalize(prev[fieldName] ?? []);
      const nextNorm = normalize(value ?? []);
      const changed = Array.isArray(prevNorm) || Array.isArray(nextNorm)
        ? JSON.stringify(prevNorm) !== JSON.stringify(nextNorm)
        : prev[fieldName] !== value;
      if (!changed) return prev;
      return { ...prev, [fieldName]: value, page: fieldName !== "page" ? 1 : prev.page };
    });
    if (fieldName === "expiry_date_range" && value === "") setRefreshKey((k) => k + 1);
  };

  const handleSort = (_, column) => {
    SetgetAllinvitedData((prev) => ({
      ...prev,
      order: prev.sort === column && prev.order === "asc" ? "desc" : "asc",
      sort: column,
    }));
  };

  const handlePageChange = ({ selected }) => {
    SetgetAllinvitedData((prev) => ({ ...prev, page: selected + 1 }));
  };

  // ─── Generate ──────────────────────────────────────────────────────────────
  const generateReport = async () => {
    try {
      setIsGenerating(true);
      setLoadData(false);
      const refreshed = await fetchReport(getAllinvitedData);
      if (refreshed?.success) {
        notify.show("Provider Expiry Report generated successfully", "success", 3000);
      } else if (refreshed?.status === 401) {
        handleUnauthorizedAccess();
      } else {
        notify.show("Unable to refresh report data. Please try again.", "error", 3000);
      }
    } catch (e) {
      notify.show("Error generating report. Please try again.", "error", 3000);
    } finally {
      setIsGenerating(false);
    }
  };

  // ─── CSV Headers ───────────────────────────────────────────────────────────
  const csvHeaders = [
    { label: "Provider ID", key: "provider_org_id" },
    { label: "Provider Name", key: "provider_name" },
    { label: "Provider Email", key: "provider_email" },
    { label: "Provider Type", key: "provider_type_name" },
    { label: "State", key: "state_name" },
    { label: "Client", key: "client_name" },
    { label: "Client Contact Name", key: "client_contact_name" },
    { label: "Client Contact Email", key: "client_contact_email" },
    { label: "Category", key: "doc_category" },
    { label: "Document Name", key: "doc_name" },
    { label: "Document Type", key: "doc_type_name" },
    { label: "Issuing Authority", key: "issuing_authority" },
    { label: "Issue Date", key: "_issue_date" },
    { label: "Policy No.", key: "policy_no" },
    { label: "Amount Insured", key: "_amount_insured" },
    { label: "Expiry Date", key: "_expiry" },
    { label: "Approval Status", key: "_status" },
  ];

  const rowToCsv = (row) => ({
    provider_org_id: row.provider_org_id || "-",
    provider_name: row.provider_name || "-",
    provider_email: row.provider_email || "-",
    provider_type_name: row.provider_type_name || "-",
    state_name: row.state_name || "-",
    client_name: row.client_name || "-",
    client_contact_name: row.client_contact_name || "-",
    client_contact_email: row.client_contact_email || "-",
    doc_category: row.doc_category || "-",
    doc_name: row.doc_name || "-",
    doc_type_name: row.doc_type_name || "-",
    issuing_authority: row.issuing_authority || "-",
    _issue_date: getIssueDate(row) ? moment(getIssueDate(row)).format("DD/MM/YYYY") : "-",
    policy_no: row.policy_no || "-",
    _amount_insured: formatAmount(row.amount_insured),
    _expiry: getExpiry(row) ? moment(getExpiry(row)).format("DD/MM/YYYY") : "-",
    _status: statusLabel(row.approval_status, row),
  });

  // ─── Export ─────────────────────────────────────────────────────────────────
  const GetAllDataForExport = async () => {
    setPdfDownloadSuccessDialog(true);
    const payload = {
      ...getAllinvitedData,
      group_by_provider: false,
      page: 1,
      limit: 10000,
      provider_org_ids: getAllinvitedData.provider_org_ids?.length
        ? JSON.stringify(getAllinvitedData.provider_org_ids.map((i) => i?.provider_org_id ?? i))
        : undefined,
      client_org_ids: getAllinvitedData.client_org_ids?.length
        ? JSON.stringify(getAllinvitedData.client_org_ids.map((i) => i?.organisation_id ?? i))
        : undefined,
      state_ids: getAllinvitedData.state_ids?.length ? JSON.stringify(getAllinvitedData.state_ids) : undefined,
    };
    try {
      const res = await dispatch(GenerateProviderExpiryReport(payload, actualPortalType));
      if (res?.success) { setPdfDownloadSuccessDialog(true); return res.data; }
      else if (res?.status === 401) handleUnauthorizedAccess();
    } catch (e) { console.error(e); }
    finally { setPdfDownloadSuccessDialog(true); }
    return null;
  };

  const handleExport = async (format) => {
    setSuccessText(format);
    const allData = await GetAllDataForExport();
    if (!allData) { setCheckPdfLoad(false); return; }
    setCheckPdfLoad(false);
    const rows = allData?.rows || [];

    // Group by Client, then Provider
    const clientsMap = {};
    rows.forEach((row) => {
      const cName = row.client_name || "Unknown Client";
      const pId = row.provider_org_id || "Unknown ID";
      const pName = row.provider_name || "Unknown Provider";
      
      if (!clientsMap[cName]) {
        clientsMap[cName] = {};
      }
      if (!clientsMap[cName][pId]) {
        clientsMap[cName][pId] = {
          provider_name: pName,
          provider_email: row.provider_email || "-",
          provider_type_name: row.provider_type_name || "-",
          state_name: row.state_name || "-",
          client_contact_name: row.client_contact_name || "-",
          client_contact_email: row.client_contact_email || "-",
          expired_docs: []
        };
      }
      clientsMap[cName][pId].expired_docs.push(row);
    });

    if (format === "csv") {
      const csvData = [];
      Object.keys(clientsMap).forEach((clientName) => {
        csvData.push(["Client:", clientName]);
        const providers = clientsMap[clientName];
        Object.keys(providers).forEach((pId) => {
          const prov = providers[pId];
          csvData.push([
            `Provider ID: ${pId}`,
            `Provider Name: ${prov.provider_name}`,
            `Provider Email: ${prov.provider_email || "-"}`,
            `Client Contact Name: ${prov.client_contact_name || "-"}`,
            `Client Contact Email: ${prov.client_contact_email || "-"}`
          ]);
          csvData.push([
            "Category",
            "Document Name",
            "Document Type",
            "Issuing Authority",
            "Issue Date",
            "Policy No.",
            "Amount Insured",
            "Expiry Date"
          ]);
          prov.expired_docs.forEach((ins) => {
            const exp = getExpiry(ins);
            const issue = getIssueDate(ins);
            csvData.push([
              ins.doc_category || "Uncategorized",
              ins.doc_name || "-",
              ins.doc_type_name || "-",
              ins.issuing_authority || "-",
              issue ? moment(issue).format("DD/MM/YYYY") : "-",
              ins.policy_no || "-",
              formatAmount(ins.amount_insured),
              exp ? moment(exp).format("DD/MM/YYYY") : "-"
            ]);
          });
          csvData.push([]);
        });
      });

      setProcessedCsvData(csvData);
      setTimeout(() => { csvLinkRef.current?.link?.click(); }, 200);
      return;
    }

    if (format === "excel") {
      const excelRows = [];
      const expiredRowIndices = [];

      Object.keys(clientsMap).forEach((clientName) => {
        excelRows.push(["Client:", clientName]);
        const providers = clientsMap[clientName];
        Object.keys(providers).forEach((pId) => {
          const prov = providers[pId];
          excelRows.push([
            `Provider ID: ${pId}`,
            `Provider Name: ${prov.provider_name}`,
            `Provider Email: ${prov.provider_email || "-"}`,
            `Client Contact Name: ${prov.client_contact_name || "-"}`,
            `Client Contact Email: ${prov.client_contact_email || "-"}`
          ]);
          excelRows.push([
            "Category",
            "Document Name",
            "Document Type",
            "Issuing Authority",
            "Issue Date",
            "Policy No.",
            "Amount Insured",
            "Expiry Date"
          ]);
          prov.expired_docs.forEach((ins) => {
            const exp = getExpiry(ins);
            const issue = getIssueDate(ins);
            const isExp = isExpiredRow(ins);
            const currentRowIndex = excelRows.length;
            if (isExp) {
              expiredRowIndices.push(currentRowIndex);
            }
            excelRows.push([
              ins.doc_category || "Uncategorized",
              ins.doc_name || "-",
              ins.doc_type_name || "-",
              ins.issuing_authority || "-",
              issue ? moment(issue).format("DD/MM/YYYY") : "-",
              ins.policy_no || "-",
              formatAmount(ins.amount_insured),
              exp ? moment(exp).format("DD/MM/YYYY") : "-"
            ]);
          });
          excelRows.push([]);
        });
      });

      const ws = XLSX.utils.aoa_to_sheet(excelRows);
      ws["!cols"] = [
        { wch: 20 },
        { wch: 30 },
        { wch: 25 },
        { wch: 25 },
        { wch: 15 },
        { wch: 20 },
        { wch: 20 },
        { wch: 15 }
      ];

      // Step: Apply red font styling to all expired cells in Excel
      expiredRowIndices.forEach((rIdx) => {
        for (let col = 0; col < 8; col++) {
          const cellRef = XLSX.utils.encode_cell({ r: rIdx, c: col });
          if (ws[cellRef]) {
            const existingStyle = ws[cellRef].s || {};
            const existingFont = existingStyle.font || {};
            ws[cellRef].s = {
              ...existingStyle,
              font: {
                ...existingFont,
                color: { rgb: "FFDC2626" },
                bold: true,
              },
            };
          }
        }
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Provider Expiry Report");
      XLSX.writeFile(wb, "provider_expiry_report.xlsx");
      return;
    }

    if (format === "pdf") {
      const doc = new jsPDF("l");
      const pageWidth = doc.internal.pageSize.getWidth();
      const today = moment().format("DD/MM/YYYY");
      
      let currentY = 15;
      
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(today, 14, currentY);
      
      const title = "Provider Expiry Report";
      doc.setFontSize(16);
      doc.setTextColor(0, 0, 0);
      doc.text(title, (pageWidth - doc.getTextWidth(title)) / 2, currentY);
      
      try {
        const logoData = await getBase64ImageFromUrl(logoPDF);
        if (logoData) doc.addImage(logoData, "PNG", pageWidth - 55, currentY - 8, 40, 10);
      } catch (_) {}
      
      currentY += 15;

      const clientNames = Object.keys(clientsMap);
      for (let cIdx = 0; cIdx < clientNames.length; cIdx++) {
        const clientName = clientNames[cIdx];
        
        if (currentY > 175) {
          doc.addPage();
          currentY = 20;
        }

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text(`Client: ${clientName}`, 14, currentY);
        currentY += 8;

        const providers = clientsMap[clientName];
        const pIds = Object.keys(providers);
        
        for (let pIdx = 0; pIdx < pIds.length; pIdx++) {
          const pId = pIds[pIdx];
          const prov = providers[pId];

          if (currentY > 175) {
            doc.addPage();
            currentY = 20;
          }

          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(50, 50, 50);
          doc.text(`Provider ID: ${pId}    Provider Name: ${prov.provider_name}    Email: ${prov.provider_email || "-"}`, 14, currentY);
          currentY += 5;
          doc.setFontSize(9);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(80, 80, 80);
          doc.text(`Client Contact: ${prov.client_contact_name || "-"}    Email: ${prov.client_contact_email || "-"}`, 14, currentY);
          currentY += 6;

          const groupedDocs = {};
          (prov.expired_docs || []).forEach((item) => {
            const rawCat = (item.doc_category || "Uncategorized").trim();
            const lowerCat = rawCat.toLowerCase();
            let cat = "Others";
            if (lowerCat === "licenses" || lowerCat === "licences") cat = "Licenses";
            else if (lowerCat === "insurances" || lowerCat === "insurance") cat = "Insurances";
            else if (lowerCat === "whs") cat = "WHS";
            else if (lowerCat === "info" || lowerCat === "information") cat = "Info";
            else if (item.document_type_id == 1 || item.document_type_id == 3) cat = "Company Registration";
            else cat = rawCat || "Others";

            if (!groupedDocs[cat]) groupedDocs[cat] = [];
            groupedDocs[cat].push(item);
          });

          Object.entries(groupedDocs).forEach(([cat, docs]) => {
            if (currentY > 175) {
              doc.addPage();
              currentY = 20;
            }
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(30, 30, 30);
            doc.text(cat, 14, currentY);
            currentY += 4;
            
            let head = [["Document Name", "Document Type", "Issuing Authority", "Issue Date", "Expiry Date"]];
            if (cat === "Insurances") {
              head = [["Document Name", "Document Type", "Issuing Authority", "Issue Date", "Policy No.", "Amount Insured", "Expiry Date"]];
            }
            
            const body = docs.map((ins) => {
              const exp = getExpiry(ins);
              const issue = getIssueDate(ins);
              const docTypeName = ins.doc_type_name || "-";
              if (cat === "Insurances") {
                return [
                  ins.doc_name || "-",
                  docTypeName,
                  ins.issuing_authority || "-",
                  issue ? moment(issue).format("DD/MM/YYYY") : "-",
                  ins.policy_no || "-",
                  formatAmount(ins.amount_insured),
                  exp ? moment(exp).format("DD/MM/YYYY") : "-",
                ];
              }
              return [
                ins.doc_name || "-",
                docTypeName,
                ins.issuing_authority || "-",
                issue ? moment(issue).format("DD/MM/YYYY") : "-",
                exp ? moment(exp).format("DD/MM/YYYY") : "-",
              ];
            });
            
            doc.autoTable({
              head,
              body,
              startY: currentY,
              styles: { fontSize: 8, overflow: "linebreak", cellPadding: 2 },
              headStyles: { fillColor: "#E5F3FE", textColor: "#464A53", fontStyle: "bold" },
              didParseCell: (data) => {
                if (data.section === "body") {
                  const docItem = docs[data.row.index];
                  if (docItem && isExpiredRow(docItem)) {
                    data.cell.styles.textColor = [220, 38, 38];
                    data.cell.styles.fontStyle = "bold";
                  }
                }
              },
              margin: { left: 14, right: 14 },
            });
            currentY = doc.lastAutoTable.finalY + 8;
          });
          currentY += 2;
        }
        currentY += 5;
      }

      doc.save("provider_expiry_report.pdf");
    }
  };

  const closeDownloadSuccessPdf = () => {
    setPdfDownloadSuccessDialog(false);
    setTimeout(() => setCheckPdfLoad(true), 1000);
  };

  const rows = reportData?.rows || [];

  // Group rows by provider_org_id + client_org_id
  const groupedRows = [];
  rows.forEach((row) => {
    const key = `${row.provider_org_id}-${row.client_org_id}`;
    let grp = groupedRows.find((g) => g.key === key);
    if (!grp) {
      grp = {
        key,
        provider_org_id: row.provider_org_id,
        provider_name: row.provider_name,
        provider_email: row.provider_email || "-",
        provider_type_name: row.provider_type_name,
        state_name: row.state_name,
        client_name: row.client_name,
        client_contact_name: row.client_contact_name || "-",
        client_contact_email: row.client_contact_email || "-",
        expired_docs: [],
      };
      groupedRows.push(grp);
    }
    if (Array.isArray(row.expired_docs)) {
      grp.expired_docs.push(...row.expired_docs);
    } else {
      grp.expired_docs.push(row);
    }
  });

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <React.Fragment>
      {/* Header */}
      <div className="flex justify-between mb-5 border-b border-gray-200 pb-5">
        <Heading>Provider Expiry Report</Heading>
        <div className="flex gap-3">
          <button
            onClick={generateReport}
            disabled={isGenerating}
            className="bg-[#5786CC] text-white text-xs px-3 py-1 rounded-md transition ml-2 flex items-center justify-center min-w-[94px] disabled:opacity-60 disabled:cursor-not-allowed hover:bg-[#3f6ab4]"
          >
            {isGenerating ? <ThreeDotLoader /> : "Generate"}
          </button>
          <div className="export-dropdow">
            <Dropdown title="Export" className="dropdown-btn">
              <Dropdown.Item eventKey="a" onClick={() => handleExport("csv")} className="text-gray-900 font font-medium">CSV</Dropdown.Item>
              <Dropdown.Item eventKey="b" onClick={() => handleExport("excel")} className="text-gray-900 font font-medium">Excel</Dropdown.Item>
              <Dropdown.Item eventKey="c" onClick={() => handleExport("pdf")} className="text-gray-900 font font-medium">PDF</Dropdown.Item>
            </Dropdown>
            {processedCsvData && (
              <CSVLink
                data={processedCsvData}
                filename="provider_expiry_report.csv"
                className="hidden"
                ref={csvLinkRef}
                target="_blank"
              />
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-1">
        <div className="flex gap-1 flex-wrap cstm-dropdown">
          {portalType !== "clientPortal" && (
            <div>
              <ClientMultiSelectDropdown
                title="Client"
                updateFilter={updateFilter}
                client_org_idsC={
                  (getAllinvitedData?.client_org_ids?.length && getAllinvitedData.client_org_ids) || []
                }
              />
            </div>
          )}
          <div>
            <AllProviderMultiSelectDropdown
              title="Provider"
              updateFilter={updateFilter}
              Path="provider"
              client_org_ids={clientOrgIdsForProvider}
            />
          </div>
          <div>
            <StatusMultiSelectDropdown
              title="Status"
              statusData={[
                { label: "Approved", value: "approved" },
                { label: "Pending", value: "pending" },
                { label: "Client Approval Required", value: "client_approval_req" },
                { label: "Admin Rejected", value: "admin_reject" },
                { label: "Client Rejected", value: "client_reject" },
                { label: "Action Required", value: "action_required" },
              ]}
              updateFilter={updateFilter}
            />
          </div>
          <div>
            <MainOfficeStateMultiSelectDropdown
              title="State"
              updateFilter={updateFilter}
            />
          </div>
          <div>
            <div className="relative flex items-center h-[42px] w-56">
              <svg className="absolute left-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <Input
                type="text"
                placeholder="Client contact name…"
                name="client_contact_name"
                className="pl-9 py-2 h-full w-full bg-white border-gray-300 rounded-lg shadow-sm text-sm"
                inputchangehandler={(val) => debouncedContactNameSearch(val)}
              />
            </div>
          </div>
          <div>
            <div className="relative flex items-center h-[42px] w-56">
              <svg className="absolute left-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
              <Input
                type="text"
                placeholder="Document name…"
                name="doc_name"
                className="pl-9 py-2 h-full w-full bg-white border-gray-300 rounded-lg shadow-sm text-sm"
                inputchangehandler={(val) => debouncedDocNameSearch(val)}
              />
            </div>
          </div>
          <div>
            <DateMultiSelectDropdown
              title="Expiry date"
              type="expiry_date_range"
              setKey={setKey}
              dateMinMax={dateMinMax}
              updateFilter={updateFilter}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto border rounded-lg border-gray-200 mt-5 max-h-[600px] relative">
        <Table className="relative border-collapse">
          <THead>
            <Tr>
              {[
                { label: "", col: null },
                { label: "Provider ID", col: "provider_org_id" },
                { label: "Provider Name", col: "provider_name" },
                { label: "Provider Email", col: null },
                { label: "Client", col: "client_name" },
                { label: "Client Contact Name", col: null },
                { label: "Client Contact Email", col: null },
              ].map(({ label, col }) => (
                <Th
                  key={label}
                  onClick={col ? (e) => handleSort(e, col) : undefined}
                  className={`sticky top-0 bg-gray-100 z-10 ${col ? "cursor-pointer select-none" : ""}`}
                >
                  <span className="flex items-center gap-1">
                    {label}
                    {col && getAllinvitedData.sort === col && (
                      <svg className={`w-3 h-3 transition-transform ${getAllinvitedData.order === "asc" ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="#475467" strokeWidth="2">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    )}
                  </span>
                </Th>
              ))}
            </Tr>
          </THead>
          <TBody>
            {groupedRows.length === 0 && loadData ? (
              <Tr><Td colSpan={7} className="text-center text-gray-500 py-8">No data available</Td></Tr>
            ) : (
              groupedRows.map((group, i) => {
                const isExpanded = expandedKeys.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <Tr className="text-gray-800 hover:bg-gray-50 transition-colors">
                      <Td className="w-12 text-center">
                        <button
                          type="button"
                          onClick={() => toggleExpand(group.key)}
                          className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 transition-colors"
                          aria-label="Expand row"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className={`transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                            aria-hidden="true"
                          >
                            <path
                              d="M6 9l6 6 6-6"
                              stroke="#475467"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </Td>
                      <Td className="font-semibold text-gray-700">{group.provider_org_id || "-"}</Td>
                      <Td className="font-medium text-gray-900">{group.provider_name || "-"}</Td>
                      <Td className="text-gray-600">{group.provider_email && group.provider_email !== "-" ? group.provider_email : "-"}</Td>
                      <Td className="font-medium text-gray-900">{group.client_name || "-"}</Td>
                      <Td className="text-gray-600">{group.client_contact_name && group.client_contact_name !== "-" ? group.client_contact_name : "-"}</Td>
                      <Td className="text-gray-600">{group.client_contact_email && group.client_contact_email !== "-" ? group.client_contact_email : "-"}</Td>
                    </Tr>
                    {isExpanded && (
                      <Tr>
                        <Td colSpan={7} className="bg-gray-50/50 p-4">
                          <div className="bg-white rounded-lg shadow-sm">
                            {(() => {
                              const groupedDocs = {};
                              (group.expired_docs || []).forEach((item) => {
                                const rawCat = (item.doc_category || "Uncategorized").trim();
                                const lowerCat = rawCat.toLowerCase();
                                
                                let cat = "Others";
                                if (lowerCat === "licenses" || lowerCat === "licences") cat = "Licenses";
                                else if (lowerCat === "insurances" || lowerCat === "insurance") cat = "Insurances";
                                else if (lowerCat === "whs") cat = "WHS";
                                else if (lowerCat === "info" || lowerCat === "information") cat = "Info";
                                else if (item.document_type_id == 1 || item.document_type_id == 3) cat = "Company Registration";
                                else cat = rawCat || "Others";

                                if (!groupedDocs[cat]) groupedDocs[cat] = [];
                                groupedDocs[cat].push(item);
                              });

                              return Object.entries(groupedDocs).map(([cat, docs]) => {
                                let cols = ["Document Name", "Document Type", "Issuing Authority", "Issue Date", "Expiry Date"];
                                if (cat === "Insurances") {
                                  cols = ["Document Name", "Document Type", "Issuing Authority", "Issue Date", "Policy No.", "Amount Insured", "Expiry Date"];
                                }

                                return (
                                  <div key={cat} className="mb-2 p-4">
                                    <h3 className="text-base text-gray-900 font-bold mb-3">{cat}</h3>
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-sm table-fixed">
                                        <thead>
                                          <tr className="text-left text-gray-600 border-b border-gray-200">
                                            {cols.map((c) => (
                                              <th key={c} className="py-2 pr-4 font-semibold whitespace-nowrap">{c}</th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {docs.map((ins, idx) => {
                                            const exp = getExpiry(ins);
                                            const issue = getIssueDate(ins);
                                            const isExp = isExpiredRow(ins);
                                            const rowTextClass = isExp ? "text-red-600 font-medium" : "text-gray-800";
                                            const docTypeName = ins.doc_type_name || "-";

                                            if (cat === "Insurances") {
                                              return (
                                                <tr key={idx} className={`hover:bg-gray-50/50 border-b border-gray-100 last:border-0 ${rowTextClass}`}>
                                                  <td className="py-2 pr-4 truncate">{ins.doc_name || "-"}</td>
                                                  <td className="py-2 pr-4 truncate">{docTypeName}</td>
                                                  <td className="py-2 pr-4 truncate">{ins.issuing_authority || "-"}</td>
                                                  <td className="py-2 pr-4 whitespace-nowrap">{issue ? moment(issue).format("DD/MM/YYYY") : "-"}</td>
                                                  <td className="py-2 pr-4 truncate">{ins.policy_no || "-"}</td>
                                                  <td className="py-2 pr-4 whitespace-nowrap">{formatAmount(ins.amount_insured)}</td>
                                                  <td className={`py-2 pr-4 whitespace-nowrap ${isExp ? "font-bold text-red-600" : ""}`}>{exp ? moment(exp).format("DD/MM/YYYY") : "-"}</td>
                                                </tr>
                                              );
                                            }
                                            return (
                                              <tr key={idx} className={`hover:bg-gray-50/50 border-b border-gray-100 last:border-0 ${rowTextClass}`}>
                                                <td className="py-2 pr-4 truncate">{ins.doc_name || "-"}</td>
                                                <td className="py-2 pr-4 truncate">{docTypeName}</td>
                                                <td className="py-2 pr-4 truncate">{ins.issuing_authority || "-"}</td>
                                                <td className="py-2 pr-4 whitespace-nowrap">{issue ? moment(issue).format("DD/MM/YYYY") : "-"}</td>
                                                <td className={`py-2 pr-4 whitespace-nowrap ${isExp ? "font-bold text-red-600" : ""}`}>{exp ? moment(exp).format("DD/MM/YYYY") : "-"}</td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex justify-between mt-3">
        <div className={!loadData ? "pointer-events-none opacity-60" : ""}>
          <Pagination
            totalCount={reportData?.total || 0}
            pageNumber={getAllinvitedData.page}
            displayPage={rows.length}
            UpdatePageLimit={SetgetAllinvitedData}
            allfilters={getAllinvitedData}
          />
        </div>
        {(reportData?.total || 0) > 0 && (
          <ReactPaginate
            className={`flex justify-content-end p-2 align-items-center pagination-wrap ${!loadData ? "pointer-events-none opacity-60" : ""}`}
            previousLabel={"Prev"}
            nextLabel={"Next"}
            breakLabel={"..."}
            breakClassName={"break-me"}
            pageCount={totalPages}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            onPageChange={handlePageChange}
            containerClassName={"pagination"}
            activeClassName={"active"}
            previousLinkClassName={"prev-link"}
            nextLinkClassName={"page-link"}
            breakLinkClassName={"prev-link"}
            pageLinkClassName={"px-4 rounded-md"}
            activeLinkClassName={"w-9 h-9 bg-blue-700 text-white"}
            disabledClassName={"px-5 opacity-50 cursor-not-allowed"}
            renderOnZeroPageCount={null}
            forcePage={getAllinvitedData.page - 1}
          />
        )}
      </div>

      {/* Loader */}
      {!loadData && (
        <div className="mt-4 flex justify-center">
          <ThreeDotBouncing />
        </div>
      )}
    </React.Fragment>
  );
};

export default ProvidersExpiry;
